import styled from '@emotion/styled'
import { CheckInviteTokenDto } from '@pharmac-extranet/extranet-api'
import { ExtranetContainer, MediaQuery, useBreakpoint } from '@pharmac-extranet/shared'
import { PharmacButton, PharmacIcon, PharmacLoadingDots } from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ExtranetGradient } from '../../components/extranet/ExtranetGradient'
import { EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY } from '../../components/inactivity-modal/InactivityModal'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { EXTRANET_CUSTOMERSHIP_STORAGE_KEY } from '../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { isValidInviteToken } from '../../utils/validate-token'
import { RegistrationView } from '../registration/RegistrationView'
import backgroundImage from './background-image/medicines.jpg'

const LandingContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-image: url(${backgroundImage});
  background-size: 170%;

  width: 100%;
  height: 90%;

  border-radius: 10px;
  box-shadow: var(--shadow-small);
  background-attachment: fixed;

  overflow: hidden;

  ${MediaQuery.minTablet} {
    flex-direction: row;
    height: 430px;
    width: 800px;
    background-size: 70%;
    background-position: center;
  }
`
const LandingOption = styled.div`
  width: 100%;
  height: 50%;

  ${MediaQuery.minTablet} {
    width: 50%;
    height: 100%;
  }
`

const LandingOptionTopHalf = styled.div`
  height: 70%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: var(--spacing-large);

  .login-welcome {
    color: var(--color-white);
    font-size: 35px;
    font-weight: 800;
  }

  .login-text {
    margin-top: var(--spacing-medium);
    text-align: center;
    color: var(--color-white);
    font-size: 25px;
    font-weight: 800;
  }

  .new-user-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--color-primary);
    border-radius: 50%;
    height: 75px;
    width: 75px;
  }

  .new-user-text {
    margin-top: var(--spacing-large);
    text-align: center;
    color: var(--color-primary);
    font-size: 25px;
    font-weight: 800;
  }
`
const LandingOptionBottomHalf = styled.div`
  height: 30%;
  padding-top: var(--spacing-small);
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const LandingOptionButton = styled(PharmacButton)`
  font-size: 18px;

  width: 70%;

  ${MediaQuery.minTablet} {
    padding: var(--spacing-medium) var(--spacing-large);
  }
`

export const NewUserLandingView = () => {
  const { minDesktop } = useBreakpoint()
  const { t, i18n } = useTranslation(['extranet', 'common'])
  const { doLogout, user, doLogin } = useAuthenticationContext()
  const [done, setDone] = useState<boolean>(false)
  const [registration, setRegistration] = useState<boolean>(false)
  const createLink = useExtranetPath()

  const { token } = useParams()
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()

  const getInviteTokenInfo = (token?: string): UseQueryOptions<CheckInviteTokenDto> => ({
    queryFn: async () => await api.customers.getInviteTokenInfo(token as string),
    queryKey: ['__inviteTokenInfo'],
    enabled: isValidInviteToken(token),
  })

  const { isLoading, data: tokenInfo } = useQuery(getInviteTokenInfo(token))

  useEffect(() => {
    const currentLanguage = i18n.language
    const languageParam = searchParams.get('lang')

    if (languageParam && languageParam !== currentLanguage) {
      i18n.changeLanguage(languageParam)
    }
  }, [])

  useEffect(() => {
    if (!isLoading && !tokenInfo) {
      return navigate(createLink('invalidInvitation'))
    }
  }, [isLoading, tokenInfo])

  useEffect(() => {
    if (user) {
      window.localStorage.removeItem(EXTRANET_CUSTOMERSHIP_STORAGE_KEY)
      window.localStorage.removeItem(EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY)

      doLogout({ redirectUri: window.location.href }).then(() => {
        setDone(true)
      })
    }
  }, [user])

  if (isLoading)
    return (
      <ExtranetContainer
        style={{ width: '100%', height: '100%' }}
        alignCenter
        justifyCenter
        paddingLarge
      >
        <PharmacLoadingDots />
      </ExtranetContainer>
    )

  const handleExistingUserLogin = () => {
    const params =
      window.location.search + (window.location.search.includes('?') ? '&ncsm=true' : '?ncsm=true')

    const url = `${window.location.origin}/liity/${token}/${params}`

    doLogin({ redirectUri: url, locale: i18n.language })
  }

  if (done || isLoading || !tokenInfo) {
    return null
  }

  if (registration) {
    return (
      <ExtranetGradient>
        <RegistrationView cancelButton={() => setRegistration(false)} />
      </ExtranetGradient>
    )
  }

  return (
    <ExtranetGradient>
      <LandingContainer>
        <LandingOption style={{ backgroundColor: '#88288FCC' }}>
          <LandingOptionTopHalf>
            <div className="login-welcome">{t('welcome', { ns: 'common' })}!</div>
            <div className="login-text">{t('inviteLinks.signUpToExtranet')}</div>
          </LandingOptionTopHalf>
          <LandingOptionBottomHalf>
            <LandingOptionButton
              onClick={() => handleExistingUserLogin()}
              style={{ backgroundColor: 'var(--color-white)', color: 'var(--color-primary)' }}
            >
              {t('common:login')}
            </LandingOptionButton>
          </LandingOptionBottomHalf>
        </LandingOption>
        <LandingOption style={{ backgroundColor: minDesktop ? '#F3E9F4CC' : '#F3E9F4' }}>
          <LandingOptionTopHalf>
            <div className="new-user-icon">
              <PharmacIcon
                size="42px"
                icon="userPlus"
                stroke="white"
              />
            </div>
            <div className="new-user-text">{t('customer.newUser')}?</div>
          </LandingOptionTopHalf>
          <LandingOptionBottomHalf>
            <LandingOptionButton onClick={() => setRegistration(true)}>
              {t('common:signUp')}
            </LandingOptionButton>
          </LandingOptionBottomHalf>
        </LandingOption>
      </LandingContainer>
    </ExtranetGradient>
  )
}
